// Templates, as stored in `templates/{id}` (Templates.swift: Template, TemplateStep), and attachments, as stored in
// `attachments/{id}` (Attachments.swift).
//
// A template is read and written as one piece, steps included. Unlike the other records, its decoder (written by
// hand in the kit) gives every missing field but the id a default, so a template never fails to load.

import { newID } from "../ids";
import { readRecord } from "./shared";
import {
  decoding, document, readArray, readStrings, readStruct, storedFields, withoutUndefined,
  type DecodeContext, type Decoded, type EncodeOptions, type Fields, type ValueReader,
} from "./values";

// MARK: - Template

/** One step: what becomes one to-do of the project made from the template. */
export interface TemplateStep {
  id: string;
  title: string;
  notes: string;
  /** The checklist's items, by title: a step's checklist is never ticked. */
  checklist: string[];
  sectionTitle?: string;
}

export interface Template {
  id: string;
  title: string;
  notes: string;
  areaID?: string;
  steps: TemplateStep[];
  /** The sections in their order, by title. */
  sections?: string[];
  lastUsedAt?: Date;
  lastAreaID?: string;
  /** "today", "anytime" or "day": what the Use sheet chose last time. */
  lastWhen?: string;
  isSample?: boolean;
  createdAt: Date;
  order: number;
  unknownFields: Fields;
}

export const TEMPLATE_FIELDS = [
  "id", "title", "notes", "areaID", "steps", "sections", "lastUsedAt", "lastAreaID", "lastWhen", "isSample", "createdAt", "order",
] as const;

/** Swift's `Date.distantPast` (1 January 0001, UTC): a template's `createdAt` when it has none. */
export const DISTANT_PAST = new Date(-62_135_596_800_000);

export function makeTemplate(values: Partial<Template> = {}): Template {
  return { id: newID(), title: "", notes: "", steps: [], createdAt: new Date(), order: 0, unknownFields: {}, ...values };
}

const readTemplateStep: ValueReader<TemplateStep> = readStruct((fields) => withoutUndefined<TemplateStep>({
  id: fields.uuid("id"),
  title: fields.optionalString("title") ?? "",
  notes: fields.optionalString("notes") ?? "",
  checklist: fields.optional("checklist", readStrings) ?? [],
  sectionTitle: fields.optionalString("sectionTitle"),
}));

export function decodeTemplate(data: Fields, context: DecodeContext): Decoded<Template> {
  return decoding(() => readRecord(data, context, (fields) => withoutUndefined<Template>({
    id: fields.uuid("id"),
    title: fields.optionalString("title") ?? "",
    notes: fields.optionalString("notes") ?? "",
    areaID: fields.optionalUUID("areaID"),
    steps: fields.optional("steps", readArray(readTemplateStep)) ?? [],
    sections: fields.optionalStrings("sections"),
    lastUsedAt: fields.optionalMoment("lastUsedAt"),
    lastAreaID: fields.optionalUUID("lastAreaID"),
    lastWhen: fields.optionalString("lastWhen"),
    isSample: fields.optionalBool("isSample"),
    createdAt: fields.optionalMoment("createdAt") ?? DISTANT_PAST,
    order: fields.optionalNumber("order") ?? 0,
    unknownFields: fields.unknownFields(TEMPLATE_FIELDS),
  })));
}

export function encodeTemplate(template: Template, options?: EncodeOptions): Fields {
  return storedFields(document(template.unknownFields, {
    id: template.id,
    title: template.title,
    notes: template.notes,
    areaID: template.areaID,
    steps: template.steps.map((step) => document({}, {
      id: step.id, title: step.title, notes: step.notes, checklist: [...step.checklist], sectionTitle: step.sectionTitle,
    })),
    sections: template.sections && [...template.sections],
    lastUsedAt: template.lastUsedAt,
    lastAreaID: template.lastAreaID,
    lastWhen: template.lastWhen,
    isSample: template.isSample,
    createdAt: template.createdAt,
    order: template.order,
  }), options);
}

// MARK: - Attachment

/** A file that belongs to a to-do (or to a template's step); the file itself is in the account's storage. */
export interface Attachment {
  id: string;
  /** The to-do's id, or the step's when `templateID` is set. */
  todoID: string;
  name: string;
  contentType: string;
  /** In bytes. */
  size: number;
  createdAt: Date;
  /** "app" or "mail"; a string, so later kinds don't break reading. */
  source?: string;
  width?: number;
  height?: number;
  isUploaded?: boolean;
  hasPreview?: boolean;
  templateID?: string;
  unknownFields: Fields;
}

export const ATTACHMENT_FIELDS = [
  "id", "todoID", "name", "contentType", "size", "createdAt", "source", "width", "height", "isUploaded", "hasPreview", "templateID",
] as const;

export function decodeAttachment(data: Fields, context: DecodeContext): Decoded<Attachment> {
  return decoding(() => readRecord(data, context, (fields) => withoutUndefined<Attachment>({
    id: fields.uuid("id"),
    todoID: fields.uuid("todoID"),
    name: fields.string("name"),
    contentType: fields.string("contentType"),
    size: fields.int("size"),
    createdAt: fields.moment("createdAt"),
    source: fields.optionalString("source"),
    width: fields.optionalInt("width"),
    height: fields.optionalInt("height"),
    isUploaded: fields.optionalBool("isUploaded"),
    hasPreview: fields.optionalBool("hasPreview"),
    templateID: fields.optionalUUID("templateID"),
    unknownFields: fields.unknownFields(ATTACHMENT_FIELDS),
  })));
}

export function encodeAttachment(attachment: Attachment, options?: EncodeOptions): Fields {
  return storedFields(document(attachment.unknownFields, {
    id: attachment.id, todoID: attachment.todoID, name: attachment.name, contentType: attachment.contentType,
    size: attachment.size, createdAt: attachment.createdAt, source: attachment.source, width: attachment.width,
    height: attachment.height, isUploaded: attachment.isUploaded, hasPreview: attachment.hasPreview,
    templateID: attachment.templateID,
  }), options);
}
